import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { Home, ClipboardList, CalendarOff, Truck, Receipt, UserCircle, Milk, Droplets, ShoppingBasket, Shirt, UtensilsCrossed, Newspaper, Package, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';
import PWAInstallPrompt from '../../components/PWAInstallPrompt';
import LanguageSelect from '../../components/language/LanguageSelect';

const items = [
  ['', Home, 'Home'],
  ['subscriptions', ClipboardList, 'Subscriptions'],
  ['vacation', CalendarOff, 'Vacation'],
  ['deliveries', Truck, 'Deliveries'],
  ['bills', Receipt, 'Bills'],
];

const serviceStyles = {
  milk: { label: 'Milk', Icon: Milk, tile: 'bg-sky-50 text-sky-600 border-sky-100' },
  water: { label: 'Water Can', Icon: Droplets, tile: 'bg-cyan-50 text-cyan-600 border-cyan-100' },
  groceries: { label: 'Groceries', Icon: ShoppingBasket, tile: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  laundry: { label: 'Laundry', Icon: Shirt, tile: 'bg-indigo-50 text-indigo-600 border-indigo-100' },
  tiffins: { label: 'Tiffins', Icon: UtensilsCrossed, tile: 'bg-amber-50 text-amber-600 border-amber-100' },
  newspaper: { label: 'Newspaper', Icon: Newspaper, tile: 'bg-slate-50 text-slate-600 border-slate-100' },
  other: { label: 'Service', Icon: Package, tile: 'bg-gray-50 text-gray-600 border-gray-100' },
};

function titleCase(value = '') {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function serviceKeyFor(subscription) {
  const product = subscription?.product || {};
  const business = subscription?.provider?.business || {};
  const text = `${product.category || ''} ${product.name || ''} ${business.business_type || ''}`.toLowerCase();
  if (text.includes('milk') || text.includes('curd') || text.includes('butter') || text.includes('dairy')) return 'milk';
  if (text.includes('water')) return 'water';
  if (text.includes('grocery') || text.includes('groceries') || text.includes('kirana') || text.includes('sugar') || text.includes('rice')) return 'groceries';
  if (text.includes('laundry') || text.includes('wash')) return 'laundry';
  if (text.includes('tiffin') || text.includes('meal') || text.includes('food')) return 'tiffins';
  if (text.includes('newspaper') || text.includes('paper')) return 'newspaper';
  return titleCase(product.category || business.business_type || product.name) || 'other';
}

function serviceLabelFor(key) {
  return serviceStyles[key]?.label || titleCase(key);
}

export default function CustomerLayout() {
  const { profile } = useAuth();
  const location = useLocation();
  const [providers, setProviders] = useState([]);
  const [serviceSubscriptions, setServiceSubscriptions] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(true);
  const linkCls = ({ isActive }) => `flex flex-col items-center gap-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${isActive ? 'text-rozops-blue' : 'text-rozops-text-muted'}`;

  useEffect(() => {
    let mounted = true;
    Promise.all([apiGet('/customer/providers'), apiGet('/customer/subscriptions?customer_id=all')])
      .then(([providersRes, subscriptionsRes]) => {
        if (!mounted) return;
        const rows = providersRes.data || [];
        setProviders(rows);
        setServiceSubscriptions(subscriptionsRes.data || []);
        const stored = localStorage.getItem('rozops_customer_provider_id');
        const storedValid = stored === 'all' || rows.some(p => p.customer_id === stored);
        const next = storedValid ? stored : (rows.length > 1 ? 'all' : (rows[0]?.customer_id || ''));
        setSelectedCustomerId(next);
        if (next) localStorage.setItem('rozops_customer_provider_id', next);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => mounted && setLoadingProviders(false));
    return () => { mounted = false; };
  }, []);

  const selectProvider = (value) => {
    setSelectedCustomerId(value);
    if (value) localStorage.setItem('rozops_customer_provider_id', value);
  };

  const selectedProvider = providers.find(p => p.customer_id === selectedCustomerId);
  const isProfilePage = location.pathname === '/my/profile';
  const serviceTiles = useMemo(() => {
    const byKey = new Map();
    serviceSubscriptions.forEach((subscription) => {
      const key = serviceKeyFor(subscription);
      const providerName = subscription.provider?.business?.business_name || 'Service provider';
      const customerId = subscription.customer_id;
      const existing = byKey.get(key) || {
        key,
        label: serviceLabelFor(key),
        customerIds: new Set(),
        providers: new Set(),
        count: 0,
      };
      if (customerId) existing.customerIds.add(customerId);
      existing.providers.add(providerName);
      existing.count += 1;
      byKey.set(key, existing);
    });
    return Array.from(byKey.values()).map((tile) => ({
      ...tile,
      customerIds: Array.from(tile.customerIds),
      providers: Array.from(tile.providers),
    }));
  }, [serviceSubscriptions]);

  const chooseServiceTile = (tile) => {
    const nextCustomerId = tile.customerIds.length === 1 ? tile.customerIds[0] : 'all';
    selectProvider(nextCustomerId);
  };

  const providerSelector = providers.length > 0 && (
    <div className="mb-4">
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
        {serviceTiles.map((tile) => {
          const style = serviceStyles[tile.key] || serviceStyles.other;
          const Icon = style.Icon;
          const active = selectedCustomerId === 'all'
            ? tile.customerIds.length > 1
            : tile.customerIds.includes(selectedCustomerId);
          return (
            <button
              key={tile.key}
              type="button"
              onClick={() => chooseServiceTile(tile)}
              className={`min-h-24 rounded-2xl border bg-white p-3 text-left shadow-soft transition-all ${active ? 'border-rozops-accent ring-2 ring-rozops-accent/15' : 'border-gray-100 hover:border-rozops-accent/40'}`}
              data-testid={`customer-service-${tile.key}`}
            >
              <span className={`w-10 h-10 rounded-2xl border grid place-items-center ${style.tile}`}>
                <Icon className="w-5 h-5" />
              </span>
              <span className="mt-3 block text-sm font-heading font-bold text-rozops-navy leading-tight">{tile.label}</span>
              <span className="mt-1 block text-[11px] text-rozops-text-muted leading-tight">
                {tile.providers.length > 1 ? `${tile.providers.length} providers` : tile.providers[0]}
              </span>
              {tile.count > 1 && <span className="mt-1 block text-[11px] font-semibold text-rozops-accent">{tile.count} subscriptions</span>}
            </button>
          );
        })}
        <Link
          to="/onboarding?addProvider=1"
          className="min-h-24 rounded-2xl border border-dashed border-rozops-accent/40 bg-white p-3 text-left shadow-soft transition-all hover:border-rozops-accent"
          data-testid="customer-add-provider"
        >
          <span className="w-10 h-10 rounded-2xl bg-rozops-accent-light text-rozops-accent grid place-items-center">
            <Plus className="w-5 h-5" />
          </span>
          <span className="mt-3 block text-sm font-heading font-bold text-rozops-navy leading-tight">Add Provider</span>
          <span className="mt-1 block text-[11px] text-rozops-text-muted leading-tight">Link another service</span>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-rozops-bg pb-20">
      <header className="bg-white border-b border-gray-100 h-14 flex items-center justify-between px-4 sticky top-0 z-40">
        <Link to="/my" className="flex items-center">
          <img src="/ro-icon.png" alt="RozOps" className="h-9 w-9 object-contain" />
        </Link>
        <div className="flex items-center gap-1">
          <LanguageSelect compact />
          <Link to="/my/profile" data-testid="customer-profile-btn" className="text-rozops-text-muted p-2 rounded-lg hover:bg-gray-50" aria-label="Profile">
            {profile?.profile_image ? <img src={profile.profile_image} alt="Profile" className="w-6 h-6 rounded-full object-cover" /> : <UserCircle className="w-5 h-5" />}
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="mb-4">
          <div className="text-sm text-rozops-text-muted">Hello,</div>
          <div className="font-heading font-bold text-xl text-rozops-navy">{profile?.full_name || 'Customer'}</div>
        </div>
        <div className="mb-4">
          <PWAInstallPrompt compact />
        </div>
        {!isProfilePage && providerSelector}
        <Outlet context={{ providers, selectedCustomerId, selectedProvider, loadingProviders }} />
        {isProfilePage && <div className="mt-4">{providerSelector}</div>}
      </main>
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-nav-top h-16 flex justify-around items-center z-40">
        {items.map(([t, Icon, l]) => (
          <NavLink key={t} to={t === '' ? '/my' : `/my/${t}`} end={t === ''} className={linkCls} data-testid={`cust-nav-${t || 'home'}`}>
            <Icon className="w-5 h-5" strokeWidth={2} /> {l}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
